import React from 'react';
import { SelectFieldComponent } from '../common/SelectFieldComponent';
import { useGeneros } from '../../hooks/useParametric';
import { type Genero } from '../../types/catalog';

export interface GeneroSelectProps {
  /** Current selected value */
  value?: number | string;
  /** Change handler */
  onChange: (value: string | number) => void;
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Custom filter function to filter generos */
  filter?: (genero: Genero) => boolean;
  /** Custom sx prop for styling */
  sx?: any;
}

/**
 * GeneroSelect Component
 *
 * A reusable dropdown component that displays generos from parametric data.
 * Features:
 * - Automatic data loading from parametric store
 * - Loading states and error handling
 * - Filtering capabilities
 * - Full TypeScript support
 * - Consistent styling with other form components
 */
export const GeneroSelect: React.FC<GeneroSelectProps> = ({
  value,
  onChange,
  label = 'Género',
  error,
  disabled = false,
  required = false,
  placeholder,
  filter,
  sx,
}) => {
  const { data: generos, loading, fetch: fetchGeneros } = useGeneros();

  // Fetch generos on component mount
  React.useEffect(() => {
    fetchGeneros();
  }, [fetchGeneros]);

  // Filter generos based on props
  const filteredGeneros = React.useMemo(() => {
    let filtered = generos;

    // Apply custom filter if provided
    if (filter) {
      filtered = filtered.filter(filter);
    }

    return filtered;
  }, [generos, filter]);

  // Convert generos to select options
  const options = React.useMemo(() => {
    return filteredGeneros.map((genero) => ({
      value: genero.idGenero,
      label: genero.tipo,
    }));
  }, [filteredGeneros]);

  // Handle error state
  const displayError = error;

  return (
    <SelectFieldComponent
      label={required ? `${label} *` : label}
      value={value || ''}
      onChange={onChange}
      options={options}
      error={displayError}
      disabled={disabled || loading}
      placeholder={placeholder}
      loading={loading}
      sx={sx}
    />
  );
};

export default GeneroSelect;